import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { trpc } from '@/api/trpc';
import { showToast } from '@/components/Layout';
import { Button, FormInput, FormSelect, Modal } from '@/components/UI';
import type { EntryOptimizerEnvelopeRun } from '../entryOptimizerUtils';

const HORIZON_OPTIONS = [
    { label: '1 day', value: String(86400) },
    { label: '3 days', value: String(3 * 86400) },
    { label: '1 week', value: String(7 * 86400) },
    { label: '2 weeks', value: String(14 * 86400) },
    { label: '1 month', value: String(30 * 86400) }
];

const OBJECTIVE_OPTIONS = [
    { label: 'Balance of good vs bad entries (recommended)', value: 'sweet_score' },
    { label: 'Average profit per entry', value: 'avg_profit' },
    { label: 'Total profit', value: 'total_profit' }
];

/**
 * Runs the tuning pass over a ready data-collection run: tests alternative entry
 * settings and validates the survivors. The setting ranges are taken from the
 * run, so only the timeframe, what to optimize for, and minimum support are asked.
 */
const StartSweepModal = ({
    run,
    onClose,
    onCreated
}: {
    run: EntryOptimizerEnvelopeRun | null;
    onClose: () => void;
    onCreated: (jobId: number) => void;
}) => {
    const [durationChoice, setDurationChoice] = useState('');
    const [horizonSeconds, setHorizonSeconds] = useState(String(7 * 86400));
    const [objective, setObjective] = useState<'avg_profit' | 'sweet_score' | 'total_profit'>('sweet_score');
    const [minUniqueEntities, setMinUniqueEntities] = useState('');
    const [minSupport, setMinSupport] = useState('');

    // Derived, not synced via effect: defaults to the run's first duration until the
    // user picks one that exists on the current run.
    const duration = run === null ? '' : run.durations.includes(durationChoice) ? durationChoice : (run.durations[0] ?? '');

    const createJobMutation = useMutation({
        ...trpc.entryOptimizer.createJob.mutationOptions(),
        onError: (error) => showToast(error.message, 'error'),
        onSuccess: (result) => {
            showToast(result.spawned ? 'Tuning started' : 'Tuning queued', 'success');
            onCreated(result.jobId);
        }
    });

    const launch = () => {
        if (!run || !duration) return;

        const parsedMinUniqueEntities = Number.parseInt(minUniqueEntities, 10);
        const parsedMinSupport = Number.parseInt(minSupport, 10);

        createJobMutation.mutate({
            envelopeExperimentId: run.experimentId,
            duration,
            horizonSeconds: Number.parseInt(horizonSeconds, 10),
            objective,
            ...(Number.isInteger(parsedMinUniqueEntities) && parsedMinUniqueEntities > 0
                ? { minUniqueEntities: parsedMinUniqueEntities }
                : {}),
            ...(Number.isInteger(parsedMinSupport) && parsedMinSupport > 0 ? { minSupport: parsedMinSupport } : {})
        });
    };

    return (
        <Modal isOpen={run !== null} title="Run tuning" onClose={onClose}>
            <div className="flex flex-col gap-4">
                <p className="text-sm text-zinc-400">
                    This tests alternative entry settings against the collected data, then double-checks the winners on data they
                    weren&apos;t tuned on so they aren&apos;t just curve-fit. It usually takes a little while.
                </p>

                <FormSelect
                    label="Timeframe"
                    options={(run?.durations ?? []).map((value) => ({ label: value, value }))}
                    value={duration}
                    onChange={(event) => setDurationChoice(event.target.value)}
                />

                <FormSelect
                    label="Hold each entry for"
                    options={HORIZON_OPTIONS}
                    value={horizonSeconds}
                    onChange={(event) => setHorizonSeconds(event.target.value)}
                />

                <FormSelect
                    label="Optimize for"
                    options={OBJECTIVE_OPTIONS}
                    value={objective}
                    onChange={(event) => setObjective(event.target.value as typeof objective)}
                />

                <details className="rounded-md border border-zinc-800 bg-black/20 p-3">
                    <summary className="cursor-pointer text-sm text-zinc-300">Advanced minimums</summary>
                    <p className="mt-2 text-xs text-zinc-500">
                        A setting is only suggested if it has enough evidence behind it. Lower these for small test universes.
                    </p>
                    <div className="mt-3 grid grid-cols-2 gap-3">
                        <FormInput
                            label="Min. distinct entities (default 10)"
                            placeholder="10"
                            value={minUniqueEntities}
                            onChange={(event) => setMinUniqueEntities(event.target.value)}
                        />
                        <FormInput
                            label="Min. entries (default 50)"
                            placeholder="50"
                            value={minSupport}
                            onChange={(event) => setMinSupport(event.target.value)}
                        />
                    </div>
                </details>

                <div className="flex justify-end gap-2">
                    <Button variant="outline-dark" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button disabled={!duration || createJobMutation.isPending} onClick={launch}>
                        {createJobMutation.isPending ? 'Starting…' : 'Run tuning'}
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default StartSweepModal;
